import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../environments/environment';

interface LoginRequest {
  CorreoUsuario: string;
  ClaveUsuario: string;
}

interface LoginSuccessResponse {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    nombreUsuario?: string;
    correoUsuario?: string;
    nombreRol?: string;
    [key: string]: unknown;
  };
  token?: string;
}

interface LoginErrorResponse {
  success: false;
  message: string;
  errors?: {
    CorreoUsuario?: string[];
    ClaveUsuario?: string[];
  };
}

interface DecodedToken {
  name?: string;
  nombre?: string;
  email?: string;
  correo?: string;
  role?: string;
  rol?: string;
  userId?: string;
  id?: string;
  exp?: number;
  iat?: number;
  [key: string]: string | number | undefined;
}

/** Credenciales rechazadas por el API (cuenta como intento fallido). */
export class LoginCredencialesError extends Error {
  constructor() {
    super('Correo o contraseña incorrectos.');
    this.name = 'LoginCredencialesError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly baseUrl = `${environment.apiUrl}/Auth`;
  private readonly tokenKey = 'trasdata_token';

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  /**
   * Realizar login en la API
   */
  login(correoUsuario: string, claveUsuario: string): Observable<string> {
    const loginRequest: LoginRequest = {
      CorreoUsuario: correoUsuario,
      ClaveUsuario: claveUsuario,
    };

    return this.http.post<LoginSuccessResponse>(`${this.baseUrl}/login`, loginRequest).pipe(
      map((response) => {
        if ((response as { success?: boolean }).success === false) {
          throw new LoginCredencialesError();
        }
        let token: string | undefined;

        // Manejar diferentes formatos de respuesta
        if (response.success && response.data?.token) {
          token = response.data.token;
        } else if (response.token) {
          token = response.token;
        }

        if (!token) {
          throw new Error('Token no encontrado en la respuesta del servidor');
        }

        return token;
      }),
      tap((token) => {
        this.clearSessionState();
        this.setToken(token);
      }),
      catchError((error) => this.handleLoginError(error))
    );
  }

  /**
   * Obtener token del almacenamiento local
   */
  getToken(): string | null {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(this.tokenKey);
    }
    return null;
  }

  /**
   * Guardar token en almacenamiento local
   */
  private setToken(token: string): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(this.tokenKey, token);
    }
  }

  /**
   * Eliminar token del almacenamiento local
   */
  removeToken(): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(this.tokenKey);
    }
  }

  /**
   * Verificar si el usuario está autenticado y el token no está expirado.
   * Si el JWT no trae `exp`, se acepta el token (no se borra): algunos entornos
   * del API emiten token sin claim de expiración y el guard dejaba al usuario en login.
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const decoded = this.decodeToken(token);
    if (!decoded) {
      // Token malformado / no es JWT
      this.removeToken();
      return false;
    }

    if (decoded.exp == null) {
      return true;
    }

    const expirationTime = Number(decoded.exp) * 1000;
    if (!Number.isFinite(expirationTime)) {
      return true;
    }

    // Margen de 60s por desfase de reloj cliente/servidor
    if (Date.now() >= expirationTime + 60_000) {
      this.removeToken();
      return false;
    }

    return true;
  }

  /**
   * Decodificar token JWT
   */
  decodeToken(token?: string): DecodedToken | null {
    const tokenToDecode = token || this.getToken();
    if (!tokenToDecode) {
      return null;
    }

    try {
      return jwtDecode<DecodedToken>(tokenToDecode);
    } catch {
      return null;
    }
  }

  /**
   * Verificar si el token está expirado.
   * Sin claim `exp`: no se considera expirado (evita logout inmediato en el interceptor).
   */
  isTokenExpired(token?: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded) {
      return true;
    }
    if (decoded.exp == null) {
      return false;
    }

    const expirationTime = Number(decoded.exp) * 1000;
    if (!Number.isFinite(expirationTime)) {
      return false;
    }

    return Date.now() >= expirationTime + 60_000;
  }

  /**
   * Obtener tiempo restante del token en milisegundos
   */
  getTokenTimeRemaining(): number {
    const token = this.getToken();
    if (!token) {
      return 0;
    }

    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) {
      return 0;
    }

    const expirationTime = Number(decoded.exp) * 1000;
    const currentTime = Date.now();

    const remaining = expirationTime - currentTime;
    return remaining > 0 ? remaining : 0;
  }

  /**
   * Obtener nombre del usuario desde el token
   */
  getUserName(): string | null {
    const decoded = this.decodeToken();
    if (!decoded) {
      return null;
    }

    const name =
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
      decoded.name ||
      decoded.nombre ||
      decoded['nombreUsuario'] ||
      decoded.email ||
      decoded.correo;

    return name ? String(name) : null;
  }

  /**
   * Obtener rol del usuario desde el token
   */
  getUserRole(): string | null {
    const decoded = this.decodeToken();
    if (!decoded) {
      return null;
    }

    // Si viene NombreRol (nombre del rol), usarlo directamente
    const roleName = decoded['NombreRol'] || decoded['nombreRol'];
    if (roleName) {
      return String(roleName);
    }

    // El rol puede venir como ID (número) o como nombre (string)
    const roleId =
      decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role'] ||
      decoded['IdRol'] ||
      decoded['IdRole'] ||
      decoded.role ||
      decoded.rol;

    return roleId ? String(roleId) : null;
  }

  /**
   * Obtener ID del usuario desde el token
   */
  getUserId(): string | null {
    const decoded = this.decodeToken();
    if (!decoded) {
      return null;
    }

    // Buscar el ID del usuario en múltiples claves posibles del token
    const userId =
      decoded['IdUsuario'] ||
      decoded['idUsuario'] ||
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
      decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/primarysid'] ||
      decoded.userId ||
      decoded.id ||
      decoded['sub'] ||
      decoded['nameid'];

    return userId ? String(userId) : null;
  }

  /**
   * Cerrar sesión
   */
  logout(): void {
    this.removeToken();
    this.clearSessionState();
    this.router.navigate(['/']);
  }

  /**
   * Borra el estado de trabajo guardado en `sessionStorage` (p. ej. caso seleccionado en Bitácora)
   * para que una nueva sesión empiece limpia.
   */
  private clearSessionState(): void {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.clear();
      }
    } catch {
      /* modo privado / sin acceso a storage */
    }
  }

  /**
   * Cerrar sesión con mensaje de expiración
   */
  logoutWithMessage(): void {
    this.removeToken();
    this.clearSessionState();
    // Usar setTimeout para evitar problemas de timing con navegación
    setTimeout(() => {
      // El mensaje se mostrará cuando se cargue la página de login
      this.router.navigate(['/'], {
        queryParams: { expired: 'true' },
      });
    }, 0);
  }

  /**
   * Manejo de errores en login
   */
  private handleLoginError(error: HttpErrorResponse | LoginCredencialesError): Observable<never> {
    if (error instanceof LoginCredencialesError) {
      return throwError(() => error);
    }
    // Mismo mensaje para correo inexistente y clave incorrecta: no revela qué correos existen.
    if (error.status === 400 || error.status === 401 || error.status === 403) {
      return throwError(() => new LoginCredencialesError());
    }

    let message = 'Ocurrió un error al iniciar sesión.';

    // Detectar si la respuesta es HTML (página de error del servidor)
    if (error.error && typeof error.error === 'string' && error.error.trim().startsWith('<')) {
      // Es una respuesta HTML, probablemente un error del servidor
      if (error.status === 500 || error.status === 502 || error.status === 503) {
        message =
          'El servidor no está disponible en este momento. Por favor, intenta más tarde o contacta al administrador.';
      } else if (error.status === 404) {
        message =
          'El servicio de autenticación no está disponible. Por favor, contacta al administrador.';
      } else {
        message = 'Error del servidor. Por favor, intenta más tarde o contacta al administrador.';
      }
      return throwError(() => new Error(message));
    }

    if (error.error) {
      const errorData = error.error as LoginErrorResponse | string;

      if (typeof errorData === 'string') {
        // Si es una cadena pero no es HTML, usar el mensaje directamente
        if (!errorData.trim().startsWith('<')) {
          message = errorData;
        } else {
          // Es HTML, usar mensaje genérico
          message = 'Error del servidor. Por favor, intenta más tarde.';
        }
      } else if (typeof errorData === 'object') {
        // Verificar si es una respuesta de error de la API
        if ('success' in errorData && errorData.success === false) {
          message = errorData.message || 'Error al iniciar sesión';
        } else if (errorData.errors) {
          // Errores de validación
          const validationErrors = errorData.errors;
          const errorMessages: string[] = [];

          if (validationErrors.CorreoUsuario) {
            errorMessages.push(...validationErrors.CorreoUsuario);
          }
          if (validationErrors.ClaveUsuario) {
            errorMessages.push(...validationErrors.ClaveUsuario);
          }

          message = errorMessages.length > 0 ? errorMessages.join('. ') : message;
        } else if (errorData.message) {
          message = errorData.message;
        }
      }
    } else if (error.status === 401) {
      message = 'Credenciales inválidas. Verifica tu correo y contraseña.';
    } else if (error.status === 0) {
      message = 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
    } else if (error.status === 500 || error.status === 502 || error.status === 503) {
      message = 'El servidor no está disponible en este momento. Por favor, intenta más tarde.';
    } else if (error.status === 404) {
      message =
        'El servicio de autenticación no está disponible. Por favor, contacta al administrador.';
    } else if (error.message && !error.message.trim().startsWith('<')) {
      message = error.message;
    }

    return throwError(() => new Error(message));
  }
}
